import React, { useMemo } from "react";
import { Box, Button, Divider, IconButton, Paper, Stack, Typography } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "shared/hooks/useTranslation";
import { useFinancialPreferences, useFinancialVisibility } from "shared/contexts/UserContext";
import { CARD_BORDER_RADIUS, CARD_ELEVATION, SCREEN_CARD_WIDTH_SX } from "shared/ui/cardConstants";
import { formatCurrencyAmountOrMasked } from "shared/money/balances";
import type { CurrencyTotals } from "features/budget/components/transactions/budget-transactions/personalTransactionUtils";

export type BudgetView = "overview" | "calendar";

export interface BalanceBudgetProps {
    currencyTotals: CurrencyTotals;
    monthLabel?: string;
    activeView?: BudgetView;
    onViewChange?: (view: BudgetView) => void;
    onPreviousMonth?: () => void;
    onNextMonth?: () => void;
}

const NET_ZERO_THRESHOLD = 0.005;

function formatSignedAmount(amount: number, currency: string, visible: boolean): string {
    const absolute = formatCurrencyAmountOrMasked(Math.abs(amount), currency, visible);
    if (!visible || Math.abs(amount) < NET_ZERO_THRESHOLD) return absolute;
    return `${amount > 0 ? "+" : "-"}${absolute}`;
}

function netColor(amount: number): string {
    if (amount > NET_ZERO_THRESHOLD) return "success.main";
    if (amount < -NET_ZERO_THRESHOLD) return "error.main";
    return "primary.main";
}

const BalanceBudget: React.FC<BalanceBudgetProps> = ({
    currencyTotals,
    monthLabel,
    activeView,
    onViewChange,
    onPreviousMonth,
    onNextMonth,
}) => {
    const { t } = useTranslation();
    const { userSettings } = useFinancialPreferences();
    const { balancesVisible } = useFinancialVisibility();
    const currencies = useMemo(() => {
        const preferred = userSettings.displayCurrency || "ARS";
        const knownOrder = [preferred, "ARS", "USD"];
        return Object.keys(currencyTotals).sort((a, b) => {
            const ai = knownOrder.indexOf(a);
            const bi = knownOrder.indexOf(b);
            if (ai !== -1 || bi !== -1) {
                return (ai === -1 ? Number.MAX_SAFE_INTEGER : ai) - (bi === -1 ? Number.MAX_SAFE_INTEGER : bi);
            }
            return a.localeCompare(b);
        });
    }, [currencyTotals, userSettings.displayCurrency]);
    const hasData = currencies.length > 0;
    const primaryCurrency = currencies[0] ?? userSettings.displayCurrency ?? "ARS";
    const title = monthLabel
        ? t("budget.monthlyBalanceFor", { month: monthLabel })
        : t("budget.monthlyBalance");
    const showViewButtons = activeView && onViewChange;

    return (
        <Paper
            elevation={CARD_ELEVATION}
            sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: CARD_BORDER_RADIUS,
                ...SCREEN_CARD_WIDTH_SX,
                overflow: "hidden",
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.84),
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                backgroundImage: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.26)}, ${alpha(theme.palette.background.paper, 0.9)})`,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, mb: 2 }}>
                <IconButton
                    onClick={onPreviousMonth}
                    aria-label={t("calendar.prevMonth")}
                    disabled={!onPreviousMonth}
                    size="small"
                    sx={{
                        flexShrink: 0,
                        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.08),
                        "&:hover": { bgcolor: (theme) => alpha(theme.palette.text.primary, 0.14) },
                    }}
                >
                    <ChevronLeftIcon />
                </IconButton>
                <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                        minWidth: 0,
                        px: 1,
                        fontSize: { xs: "1rem", sm: "1.2rem" },
                        fontWeight: 800,
                        textAlign: "center",
                    }}
                >
                    {title}
                </Typography>
                <IconButton
                    onClick={onNextMonth}
                    aria-label={t("calendar.nextMonth")}
                    disabled={!onNextMonth}
                    size="small"
                    sx={{
                        flexShrink: 0,
                        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.08),
                        "&:hover": { bgcolor: (theme) => alpha(theme.palette.text.primary, 0.14) },
                    }}
                >
                    <ChevronRightIcon />
                </IconButton>
            </Box>

            {!hasData ? (
                <Box
                    sx={{
                        py: 3,
                        px: 2,
                        textAlign: "center",
                        borderRadius: CARD_BORDER_RADIUS,
                        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04),
                        border: (theme) => `1px dashed ${alpha(theme.palette.primary.main, 0.28)}`,
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        {formatCurrencyAmountOrMasked(0, primaryCurrency, balancesVisible)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t("budget.noTransactions")}
                    </Typography>
                </Box>
            ) : (
                <Stack
                    spacing={0}
                    divider={<Divider sx={{ borderColor: (theme) => alpha(theme.palette.primary.main, 0.14) }} />}
                >
                    {currencies.map((cur) => {
                        const { spent, received } = currencyTotals[cur];
                        const net = received - spent;
                        return (
                            <Box
                                key={cur}
                                sx={{
                                    py: { xs: 1.75, sm: 2 },
                                    "&:first-of-type": { pt: 0.5 },
                                    "&:last-of-type": { pb: 0.5 },
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mb: 1,
                                        fontWeight: 900,
                                        color: "text.primary",
                                        textAlign: "center",
                                        letterSpacing: 0,
                                    }}
                                >
                                    {cur}
                                </Typography>
                                <Box sx={{ textAlign: "center" }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                                        {t("budget.netBalance")}
                                    </Typography>
                                    <Typography
                                        component="p"
                                        sx={{
                                            mt: 0.5,
                                            fontWeight: 900,
                                            lineHeight: 1.05,
                                            fontSize: { xs: "2rem", sm: "2.65rem" },
                                            color: netColor(net),
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {formatSignedAmount(net, cur, balancesVisible)}
                                    </Typography>
                                </Box>

                                <Divider sx={{ my: 1.75, borderColor: (theme) => alpha(theme.palette.primary.main, 0.12) }} />

                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        alignItems: "start",
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, textAlign: "center", pr: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {t("budget.totalSpent")}
                                        </Typography>
                                        <Typography sx={{ mt: 0.25, fontWeight: 800, color: "error.main", overflowWrap: "anywhere" }}>
                                            {formatCurrencyAmountOrMasked(spent, cur, balancesVisible)}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            minWidth: 0,
                                            textAlign: "center",
                                            pl: 1,
                                            borderLeft: 1,
                                            borderColor: (theme) => alpha(theme.palette.primary.main, 0.14),
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            {t("budget.totalReceived")}
                                        </Typography>
                                        <Typography sx={{ mt: 0.25, fontWeight: 800, color: "success.main", overflowWrap: "anywhere" }}>
                                            {formatCurrencyAmountOrMasked(received, cur, balancesVisible)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Stack>
            )}

            {showViewButtons && (
                <>
                    <Divider sx={{ mt: 2, mb: 1.5 }} />
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            p: 1,
                            borderRadius: 999,
                            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.1 : 0.08),
                        }}
                    >
                        <Button
                            fullWidth
                            variant={activeView === "overview" ? "contained" : "outlined"}
                            color="primary"
                            startIcon={<ReceiptLongIcon />}
                            onClick={() => onViewChange("overview")}
                            aria-pressed={activeView === "overview"}
                            sx={{ minHeight: 48, borderRadius: 999, boxShadow: activeView === "overview" ? 4 : 0 }}
                        >
                            {t("budget.tabOverview")}
                        </Button>
                        <Button
                            fullWidth
                            variant={activeView === "calendar" ? "contained" : "outlined"}
                            color="primary"
                            startIcon={<CalendarMonthIcon />}
                            onClick={() => onViewChange("calendar")}
                            aria-pressed={activeView === "calendar"}
                            sx={{ minHeight: 48, borderRadius: 999, boxShadow: activeView === "calendar" ? 4 : 0 }}
                        >
                            {t("navigation.personalCalendar")}
                        </Button>
                    </Stack>
                </>
            )}
        </Paper>
    );
};

export default BalanceBudget;
